export type CategoryLanguage = "pt" | "en" | "es" | "it";

/**
 * System category names are stored in Portuguese so historical records and AI
 * categorization keep stable identifiers. This map only changes what is shown
 * in the interface; it never modifies persisted financial data.
 */
const categoryTranslations: Record<string, Record<Exclude<CategoryLanguage, "pt">, string>> = {
  "Alimentação": { en: "Food", es: "Alimentación", it: "Alimentazione" },
  "Educação": { en: "Education", es: "Educación", it: "Istruzione" },
  "Finanças": { en: "Finance", es: "Finanzas", it: "Finanze" },
  "Lazer": { en: "Leisure", es: "Ocio", it: "Tempo libero" },
  "Moradia": { en: "Housing", es: "Vivienda", it: "Casa" },
  "Pets": { en: "Pets", es: "Mascotas", it: "Animali domestici" },
  "Saúde": { en: "Health", es: "Salud", it: "Salute" },
  "Transporte": { en: "Transportation", es: "Transporte", it: "Trasporti" },
  "Vestuário": { en: "Clothing", es: "Ropa", it: "Abbigliamento" },
  "Outros": { en: "Other", es: "Otros", it: "Altro" },
  "Salário": { en: "Salary", es: "Salario", it: "Stipendio" },
  "Aposentadoria": { en: "Retirement", es: "Jubilación", it: "Pensione" },
  "Freelance": { en: "Freelance", es: "Freelance", it: "Freelance" },
  "Pensão": { en: "Pension", es: "Pensión", it: "Pensione" },
  "Poupança": { en: "Savings", es: "Ahorros", it: "Risparmi" },
  "Reserva de Emergência": { en: "Emergency Fund", es: "Fondo de emergencia", it: "Fondo di emergenza" },
  "Investimentos": { en: "Investments", es: "Inversiones", it: "Investimenti" },
  "Viagem": { en: "Travel", es: "Viajes", it: "Viaggi" },
  "Manutenção Residencial": { en: "Home Maintenance", es: "Mantenimiento del hogar", it: "Manutenzione della casa" },
  "Gastos Pessoais": { en: "Personal Expenses", es: "Gastos personales", it: "Spese personali" },
  "Doação": { en: "Donation", es: "Donación", it: "Donazione" },
  "Presentes": { en: "Gifts", es: "Regalos", it: "Regali" },
  "Casa e Decoração": { en: "Home & Decor", es: "Hogar y decoración", it: "Casa e arredamento" },
  "Empréstimos": { en: "Loans", es: "Préstamos", it: "Prestiti" },
};

export function translateCategoryName(name: string | null | undefined, language: CategoryLanguage): string {
  if (!name || language === "pt") return name ?? "";
  return categoryTranslations[name]?.[language] ?? name;
}

export const SYSTEM_CATEGORY_NAMES = {
  income: ["Salário", "Aposentadoria", "Pensão", "Freelance", "Outros"],
  transfer: ["Poupança", "Reserva de Emergência", "Investimentos"],
} as const;

// O banco mantém duas categorias com o mesmo nome, uma para cada tipo.
// Esses IDs são categorias padrão estáveis criadas na base do Bolso.
export const LOAN_CATEGORY_IDS = {
  income: 120002,
  expense: 120003,
} as const;

export function isCategoryForType(category: { id: number; name: string; kind?: "income" | "expense" | "transfer" | null }, type: "income" | "expense" | "transfer"): boolean {
  if (category.kind) return category.kind === type;
  if (category.name === "Empréstimos") return category.id === LOAN_CATEGORY_IDS[type === "income" ? "income" : "expense"];
  if (type === "income") return (SYSTEM_CATEGORY_NAMES.income as readonly string[]).includes(category.name);
  if (type === "transfer") return (SYSTEM_CATEGORY_NAMES.transfer as readonly string[]).includes(category.name);
  return !(SYSTEM_CATEGORY_NAMES.income as readonly string[]).includes(category.name)
    && !(SYSTEM_CATEGORY_NAMES.transfer as readonly string[]).includes(category.name);
}

export function getCategoryKind(name: string): "income" | "transfer" | "expense" {
  if ((SYSTEM_CATEGORY_NAMES.income as readonly string[]).includes(name)) return "income";
  if ((SYSTEM_CATEGORY_NAMES.transfer as readonly string[]).includes(name)) return "transfer";
  return "expense";
}
